import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Paper,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  ButtonBase,
  stepConnectorClasses,
} from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useParams } from "react-router-dom";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import moment from "moment";
import jwtDecode from "jwt-decode";
import OrderDetail from "./OrderDetail";
import OrderAssign from "./OrderAssign";
import FormPageHeader from "../../utilities/FormPageHeader";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import OAuthService from "src/app/auth/services/oAuthService";



const BASE_STEPS = [
  { status: "New", value: "NEW" },
  { status: "Assigned", value: "ASSIGNED" },
  { status: "In Progress", value: "IN_PROGRESS" },
  { status: "Order Placed", value: "ORDER_PLACED" },
  { status: "ACK From Factory", value: "ACK_FRM_FACTORY" },
  { status: "AB Received", value: "AB_RECEIVED" },
  { status: "Under Confirmation", value: "UNDER_CONFIRMATION" },
  { status: "Confirmed", value: "CONFIRMED" },
  { status: "Ready to Book", value: "READY_TO_BOOK" },
  { status: "Production Planning", value: "PRODUCTION_PLANNING" },
  { status: "PI Generated", value: "PI_GENERATED" },
  { status: "Ready to Release", value: "READY_TO_RELEASE" },
  { status: "Released", value: "RELEASED" },
  { status: "Loaded", value: "LOADED" },
  { status: "Invoiced", value: "INVOICED" },
];

const AMENDMENT_NFZE = {
  status: "Amendment Send to NFZE",
  value: "AMENDMENT_REQUEST_NFZE",
};
const AMENDMENT_FACTORY = {
  status: "Amendment Send to Factory",
  value: "AMENDMENT_REQUEST_FACTORY",
};

// step value -> field on the order response that holds its date
const STEP_DATE_FIELDS = {
  NEW: "orderCreationDate",
  ASSIGNED: "assignedDttm",
  IN_PROGRESS: "orderInProgressDate",
  ORDER_PLACED: "orderPlacedDate",
  ACK_FRM_FACTORY: "orderAckFrmFactoryDate",
  AB_RECEIVED: "abReceivedDate",
  UNDER_CONFIRMATION: "customerConfirmedDate",
  CONFIRMED: "orderConfirmedDate",
  PI_GENERATED: "orderPiGeneratedDate",
  RELEASED: "releaseDate",
  LOADED: "actualLoadingDate",
};

const PARTNER_ROLES = [
  "ROLE_PARTNER",
  "ROLE_PARTNER_ADMIN",
  "ROLE_SHWRMMGR",
  "ROLE_PARTNRDESIGN",
  "ROLE_PARTNRORDPROCESS",
];

const EDIT_ROLES_EARLY_STATUS = [
  "ROLE_MD",
  "ROLE_SOPDIR",
  "ROLE_ADMIN",
  "ROLE_ORDERPRO",
  "ROLE_PARTNRORDPROCESS",
  "ROLE_SHWRMMGR",
  "ROLE_PARTNER_ADMIN",
];
const EDIT_ROLES_DEFAULT = [
  "ROLE_MD",
  "ROLE_SOPDIR",
  "ROLE_ADMIN",
  "ROLE_ORDERPRO",
  "ROLE_LOGIMGR",
  "ROLE_LOGISTICS",
];
const DELETE_ROLES = ["ROLE_MD", "ROLE_SOPDIR", "ROLE_ADMIN"];

const STEP_WIDTH = 112; // px, desktop step width



const formatDate = (date) => (date ? moment(date).format("DD-MM-YYYY") : "");

const hasAnyRole = (token, roles) =>
  roles.some((role) => token?.scope?.includes(role));

const readToken = () => {
  try {
    const raw = localStorage.getItem("token");
    return raw ? jwtDecode(raw) : null;
  } catch {
    return null;
  }
};

const buildSteps = (order) => {
  const steps = BASE_STEPS.map((s) => ({ ...s }));

  if (order?.amendmentStatus === "AMENDMENT_REQUEST_NFZE") {
    const idx = steps.findIndex((s) => s.value === "AB_RECEIVED");
    steps.splice(idx + 1, 0, { ...AMENDMENT_NFZE });
  }
  if (order?.amendmentStatus === "AMENDMENT_REQUEST_FACTORY") {
    const idx = steps.findIndex((s) => s.value === "UNDER_CONFIRMATION");
    steps.splice(idx + 1, 0, { ...AMENDMENT_FACTORY });
  }

  return steps.map((s) => ({
    ...s,
    date: order?.[STEP_DATE_FIELDS[s.value]] || "",
  }));
};



const ProgressConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 13,
    left: "calc(-50% + 18px)",
    right: "calc(50% + 18px)",
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    borderRadius: 2,
    backgroundColor: theme.palette.divider,
    transition: "background-color .3s ease",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}, &.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]:
    {
      backgroundColor: theme.palette.success.main,
    },
  // vertical orientation (mobile list)
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 13,
  },
  [`&.${stepConnectorClasses.vertical} .${stepConnectorClasses.line}`]: {
    width: 3,
    height: "100%",
    minHeight: 18,
  },
}));

const StepDot = ({ active, completed, icon }) => {
  const theme = useTheme();
  const size = 28;

  let bg = theme.palette.background.paper;
  let color = theme.palette.text.disabled;
  let border = `2px solid ${theme.palette.divider}`;
  let shadow = "none";

  if (completed) {
    bg = theme.palette.success.main;
    color = theme.palette.success.contrastText;
    border = `2px solid ${theme.palette.success.main}`;
  } else if (active) {
    bg = theme.palette.secondary.main;
    color = theme.palette.secondary.contrastText;
    border = `2px solid ${theme.palette.secondary.main}`;
    shadow = `0 0 0 5px ${alpha(theme.palette.secondary.main, 0.2)}`;
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        bgcolor: bg,
        color,
        border,
        boxShadow: shadow,
        transition: "all .25s ease",
        zIndex: 1,
      }}
    >
      {completed ? (
        <FuseSvgIcon size={16} sx={{ color: "inherit" }}>
          heroicons-solid:check
        </FuseSvgIcon>
      ) : (
        icon
      )}
    </Box>
  );
};



const OrderProgress = ({ steps, activeIndex }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);

  const safeIndex = activeIndex < 0 ? 0 : activeIndex;
  const current = steps[safeIndex];
  const percent = steps.length > 1 ? ((safeIndex + 1) / steps.length) * 100 : 0;

  // keep the active step centered in the scroll area on desktop/tablet
  useEffect(() => {
    if (isMobile) return;
    const container = scrollRef.current;
    const el = activeRef.current;
    if (!container || !el) return;
    const target = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;
    container.scrollTo({ left: Math.max(target, 0), behavior: "smooth" });
  }, [safeIndex, steps.length, isMobile]);

  if (isMobile) {
    return (
      <Paper
        variant="outlined"
        sx={{ width: "100%", my: 1, borderRadius: 2, overflow: "hidden" }}
      >
        <ButtonBase
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="order-progress-list"
          sx={{
            width: "100%",
            display: "block",
            textAlign: "left",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Step {safeIndex + 1} of {steps.length}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
                noWrap
              >
                {current?.status}
              </Typography>
              {current?.date && (
                <Typography variant="caption" color="text.secondary">
                  {formatDate(current.date)}
                </Typography>
              )}
            </Box>
            <FuseSvgIcon
              size={20}
              sx={{
                flexShrink: 0,
                color: "text.secondary",
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform .2s ease",
              }}
            >
              heroicons-solid:chevron-down
            </FuseSvgIcon>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percent}
            color="success"
            sx={{ mt: 1.25, height: 6, borderRadius: 3 }}
          />
        </ButtonBase>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            id="order-progress-list"
            sx={{
              px: 1.5,
              pb: 1.5,
              maxHeight: 320,
              overflowY: "auto",
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 1.5,
            }}
          >
            <Stepper
              orientation="vertical"
              activeStep={safeIndex}
              connector={<ProgressConnector />}
            >
              {steps.map((step) => (
                <Step key={step.value}>
                  <StepLabel
                    StepIconComponent={StepDot}
                    sx={{
                      py: 0,
                      "& .MuiStepLabel-label": {
                        fontSize: 13,
                        lineHeight: 1.3,
                      },
                      "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
                    }}
                    optional={
                      step.date ? (
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(step.date)}
                        </Typography>
                      ) : null
                    }
                  >
                    {step.status}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Collapse>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ width: "100%", my: 1, borderRadius: 2, overflow: "hidden" }}
    >
      <Box
        ref={scrollRef}
        sx={{
          overflowX: "auto",
          px: 1,
          pt: 2,
          pb: 1.5,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.divider,
            borderRadius: 3,
          },
        }}
      >
        <Stepper
          activeStep={safeIndex}
          alternativeLabel
          connector={<ProgressConnector />}
          sx={{ minWidth: steps.length * STEP_WIDTH }}
        >
          {steps.map((step, i) => (
            <Step
              key={step.value}
              ref={i === safeIndex ? activeRef : null}
              sx={{ minWidth: STEP_WIDTH, px: 0.5 }}
            >
              <StepLabel
                StepIconComponent={StepDot}
                sx={{
                  "& .MuiStepLabel-label": {
                    fontSize: 12,
                    lineHeight: 1.25,
                    mt: "8px !important",
                    color: "text.secondary",
                  },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: "text.primary",
                    fontWeight: 700,
                  },
                  "& .MuiStepLabel-label.Mui-completed": {
                    color: "text.primary",
                    fontWeight: 500,
                  },
                }}
              >
                {step.status}
                {step.date && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.25, fontWeight: 400 }}
                  >
                    {formatDate(step.date)}
                  </Typography>
                )}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Paper>
  );
};



const ActionButton = ({ title, icon, ...rest }) => (
  <Tooltip title={title}>
    {/* span lets the tooltip work on disabled buttons */}
    <span>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        aria-label={title}
        sx={{ minWidth: 36, px: 1 }}
        {...rest}
      >
        <FuseSvgIcon size={18}>{icon}</FuseSvgIcon>
      </Button>
    </span>
  </Tooltip>
);



const OrderDetails = () => {
  const childRef = useRef();
  const params = useParams();

  const [open, setOpen] = useState(false);
  const [saveDisable, setSaveDisable] = useState(false);
  const [, setOrderType] = useState();
  const [successMsg, setSuccessMsg] = useState();
  const [snackState, setSnackState] = useState();
  const [currentOrder, setCurrentOrder] = useState();
  const [error, setError] = useState();
  const [orderStatus, setOrderStatus] = useState();
  const [disableData, setDisableData] = useState(false);
  const [steps, setSteps] = useState(() => buildSteps(null));

  const orderUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.orders}`;
  const token = useMemo(readToken, []);
  const hasPartnersRole = hasAnyRole(token, PARTNER_ROLES);


  const handleClose = () => setOpen(false);
  const handleEditOrder = () => setDisableData(true);
  const handleCancelEdit = () => setDisableData(false);
  const handleBack = () => window.history.back();
  const handleUpdate = () => childRef.current?.handleUpdateOrder();
  const handleDelete = () => childRef.current?.handleDeleteOrder();

  const resolveOrderStatus = (data) => {
    if (
      hasPartnersRole &&
      data?.orderStatus === "AB_RECEIVED" &&
      data?.abShareDate === null
    ) {
      return "ACK_FRM_FACTORY";
    }
    if (data?.amendmentStatus === "AMENDMENT_REQUEST_NFZE") {
      return "AMENDMENT_REQUEST_NFZE";
    }
    if (data?.amendmentStatus === "AMENDMENT_REQUEST_FACTORY") {
      return "AMENDMENT_REQUEST_FACTORY";
    }
    return data?.orderStatus;
  };

  const loadOrder = useCallback(async () => {
    const res = await OAuthService.getEditData(orderUrl, params.id);
    const data = res?.data;
    setCurrentOrder(data);
    setOrderStatus(resolveOrderStatus(data));
    setSteps(buildSteps(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderUrl, params.id, hasPartnersRole]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleSuccessMsg = (msg) => {
    setSuccessMsg(msg);
    loadOrder();
    setSnackState(Boolean(msg));
  };
  const handleError = (err) => setError(err);
  const handleSaveDisabled = (st) => setSaveDisable(st);


  const activeStepIndex = steps.findIndex((s) => s.value === orderStatus);

  const editAllowed = useMemo(() => {
    const isEarly = ["ASSIGNED", "NEW", "HOLD"].includes(orderStatus);
    if (isEarly && hasAnyRole(token, EDIT_ROLES_EARLY_STATUS)) return true;
    return hasAnyRole(token, EDIT_ROLES_DEFAULT);
  }, [orderStatus, token]);

  const canDelete = hasAnyRole(token, DELETE_ROLES);


  const orderHeaderAction = () => (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          gap: 1,
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <ActionButton
          title="Back"
          icon="heroicons-solid:arrow-narrow-left"
          onClick={handleBack}
        />

        {disableData ? (
          <ActionButton
            title="Done editing"
            icon="material-solid:edit_off"
            onClick={handleCancelEdit}
          />
        ) : (
          <ActionButton
            title="Edit order"
            icon="heroicons-solid:pencil"
            onClick={handleEditOrder}
            disabled={!editAllowed}
          />
        )}

        <ActionButton
          title="Save order"
          icon="material-solid:save"
          onClick={handleUpdate}
          disabled={saveDisable}
        />

        {canDelete && (
          <ActionButton
            title="Delete order"
            icon="material-solid:delete"
            onClick={handleDelete}
          />
        )}
      </Box>

      <OrderAssign open={open} handleClose={handleClose} />
    </>
  );

  const pageTitle = currentOrder
    ? `#${currentOrder.commissionName || ""}${
        currentOrder.abNumber ? ` - ${currentOrder.abNumber}` : ""
      }`
    : undefined;

  return (
    <>
      <FormPageHeader
        componentName="View Order"
        pageName="View Order"
        currentPage={pageTitle}
        specialDiscount={currentOrder?.sdrStatus}
        successMsg={successMsg}
        error={error}
        snackState={snackState}
        orderHeaderAction={orderHeaderAction()}
      >
        <OrderProgress steps={steps} activeIndex={activeStepIndex} />
      </FormPageHeader>

      <OrderDetail
        ref={childRef}
        handleSaveDisabled={handleSaveDisabled}
        setOrderType={setOrderType}
        handleSuccessMsg={handleSuccessMsg}
        handleError={handleError}
        disableData={disableData}
      />
    </>
  );
};

export default OrderDetails;